import { Alert, Button, Card, Form, Input, Space, Tag, Typography } from "antd";
import type { ComponentProps, ComponentType } from "react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import {
    Button as ZhButton,
    Dialog as ZhDialog,
    InputNumber as ZhInputNumber,
    Pagination as ZhPagination,
    Table as ZhTable,
    type TableColumnProps
} from "zhihao-ui-react";

import request from "@/api";
import { useUserStore } from "@/views/stores";
import { TableSearchIcon } from "@/views/components/TableSearchIcon";
import type { ConsignmentQuery, GroupPurchasePageResult, GroupPurchaseRow } from "./types";

const defaultQuery: ConsignmentQuery = { page: 1, size: 10 };

// beta.13 的运行时支持 controls 配置，类型声明暂未暴露该兼容字段。
type ProductWeightInputProps = ComponentProps<typeof ZhInputNumber> & { controls?: boolean };
const ProductWeightInput = ZhInputNumber as ComponentType<ProductWeightInputProps>;
const groupStateLabels: Record<number, string> = { 1: "进行中", 2: "已完成", 3: "已结束" };
const productStateLabels: Record<number, string> = { 1: "上架", 2: "下架" };
const publishStateLabels: Record<number, string> = {
    1: "待审核",
    2: "审核失败",
    3: "已发布",
    4: "已下架",
    5: "已关闭",
    6: "待提交"
};

type GroupPurchaseFilterValues = {
    keyword?: string;
};

type GroupPurchaseSpecialist = {
    user: { personId?: number; name?: string; employeeId?: string };
    productCount?: number;
};

/** Vue 下架确认弹窗展示的团购订单统计契约。 */
export interface GroupPurchaseDownShelfInfo {
    totalOrderNum?: number;
    obligationOrderNum?: number;
    toBeConfirmedOrderNum?: number;
    groupingOrderNum?: number;
}

export interface GroupPurchasePageProps {
    assignGroupPurchase?: (id: number | string, handlerId: number) => Promise<void>;
    closeGroupPurchase?: (id: number | string, unActiveFileIds?: number[]) => Promise<void>;
    downShelfGroupPurchase?: (id: number | string) => Promise<void>;
    findServiceSpecials?: (keyword: string) => Promise<GroupPurchaseSpecialist[]>;
    loadDownShelfInfo?: (id: number | string) => Promise<GroupPurchaseDownShelfInfo>;
    loadGroupPurchases?: (query: ConsignmentQuery) => Promise<GroupPurchasePageResult>;
    updateWeight?: (params: { id: number | string; weight: number }) => Promise<void>;
    permissions?: string[];
}

/** 复用 Vue 团购商品分页接口。 */
export async function loadGroupPurchases(query: ConsignmentQuery): Promise<GroupPurchasePageResult> {
    const response = await request.service.get<GroupPurchasePageResult>("/admin/product-group-purchase/paging", {
        params: query
    });
    return response.data;
}

/** 复用 Vue 下架确认弹窗查询参团订单统计的详情接口。 */
export async function loadGroupPurchaseDownShelfInfo(id: number | string): Promise<GroupPurchaseDownShelfInfo> {
    const response = await request.service.get<GroupPurchaseDownShelfInfo>(`/admin/product-group-purchase/vo/${id}`);
    return response.data;
}

/** 复用 Vue 团购商品权重更新接口。 */
export async function updateGroupPurchaseWeight(params: { id: number | string; weight: number }): Promise<void> {
    await request.service.put(`/admin/product-group-purchase/weight/${params.id}`, params);
}

/** 复用 Vue 团购商品下架接口。 */
export async function downShelfGroupPurchase(id: number | string): Promise<void> {
    await request.service.put(`/admin/product-group-purchase/down-shelf/${id}`);
}

/** 复用 Vue 团购商品关闭接口，并提交需要解除关联的文件 ID。 */
export async function closeGroupPurchase(id: number | string, unActiveFileIds: number[] = []): Promise<void> {
    await request.service.put(`/admin/product-group-purchase/close/${id}`, { unActiveFileIds });
}

/** 复用 Vue 团购专员候选查询接口。 */
export async function findServiceSpecials(keyword: string): Promise<GroupPurchaseSpecialist[]> {
    const response = await request.service.get<GroupPurchaseSpecialist[]>(
        `/admin/product-group-purchase/find/specials?name=${encodeURIComponent(keyword)}`
    );
    return response.data;
}

/** 复用 Vue 团购商品指派接口。 */
export async function assignGroupPurchase(id: number | string, handlerId: number): Promise<void> {
    await request.service.get(`/admin/product-group-purchase/assign/${id}/${handlerId}`);
}

/** 将 Vue 团购商品列表返回的团购、商品和发布状态枚举统一为数字。 */
function normalizeGroupPurchaseRows(rows?: GroupPurchaseRow[]): GroupPurchaseRow[] {
    return (rows || []).map(row => ({
        ...row,
        productGroupPurchase: {
            ...row.productGroupPurchase,
            groupPurchaseState:
                row.productGroupPurchase.groupPurchaseState == null
                    ? undefined
                    : Number(row.productGroupPurchase.groupPurchaseState),
            state: row.productGroupPurchase.state == null ? undefined : Number(row.productGroupPurchase.state),
            publishState:
                row.productGroupPurchase.publishState == null
                    ? undefined
                    : Number(row.productGroupPurchase.publishState)
        }
    }));
}

/** 团购商品列表，保留 Vue 首屏字段、权限和复制入口。 */
export function GroupPurchasePage({
    assignGroupPurchase: assign = assignGroupPurchase,
    closeGroupPurchase: close = closeGroupPurchase,
    downShelfGroupPurchase: removeFromShelf = downShelfGroupPurchase,
    findServiceSpecials: findSpecials = findServiceSpecials,
    loadDownShelfInfo: loadInfo = loadGroupPurchaseDownShelfInfo,
    loadGroupPurchases: load = loadGroupPurchases,
    updateWeight: saveWeight = updateGroupPurchaseWeight,
    permissions
}: GroupPurchasePageProps) {
    const navigate = useNavigate();
    const storePermissions = useUserStore(state => state.userInfo.permissions || []);
    const banButton = useUserStore(state => state.banButton);
    const currentPermissions = permissions || storePermissions;
    const canDeal = currentPermissions.includes("ROLE_PRODUCT_DEAL");
    const canManage = currentPermissions.includes("ROLE_PRODUCT_MANAGE");
    const canClosePermission = currentPermissions.includes("ROLE_PRODUCT_CLOSE");
    const [items, setItems] = useState<GroupPurchaseRow[]>([]);
    const [total, setTotal] = useState(0);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [assignTarget, setAssignTarget] = useState<GroupPurchaseRow | null>(null);
    const [specialists, setSpecialists] = useState<GroupPurchaseSpecialist[]>([]);
    const [specialistKeyword, setSpecialistKeyword] = useState("");
    const [selectedHandlerId, setSelectedHandlerId] = useState<number | undefined>();
    const [assignError, setAssignError] = useState<string | null>(null);
    const [assignLoading, setAssignLoading] = useState(false);
    const [downShelfTargetId, setDownShelfTargetId] = useState<number | string | null>(null);
    const [downShelfInfo, setDownShelfInfo] = useState<GroupPurchaseDownShelfInfo>();
    const [downShelfLoading, setDownShelfLoading] = useState(false);
    const [downShelfError, setDownShelfError] = useState<string | null>(null);
    const [query, setQuery] = useState<ConsignmentQuery>(defaultQuery);
    const [filterForm] = Form.useForm<GroupPurchaseFilterValues>();

    useEffect(() => {
        let disposed = false;
        void load(query)
            .then(result => {
                if (!disposed) {
                    setErrorMessage(null);
                    setItems(normalizeGroupPurchaseRows(result.list));
                    setTotal(result.total || 0);
                }
            })
            .catch(() => {
                if (!disposed) {
                    setErrorMessage("团购商品加载失败");
                    setItems([]);
                    setTotal(0);
                }
            });
        return () => {
            disposed = true;
        };
    }, [load, query]);

    // 将旧 Vue 商品编号/标题筛选转换为团购分页接口的 `keyword` 参数。
    const submitFilters = ({ keyword }: GroupPurchaseFilterValues) => {
        setErrorMessage(null);
        setQuery({ ...defaultQuery, ...(keyword ? { keyword } : {}) });
    };

    // 清空关键词并恢复旧 Vue 首屏分页参数。
    const resetFilters = () => {
        filterForm.resetFields();
        setQuery(defaultQuery);
    };

    // 用 zhihao-ui-react 列契约承接 Vue 团购商品的嵌套字段、状态枚举和权限动作。
    const columns: TableColumnProps<GroupPurchaseRow>[] = [
        {
            key: "id",
            label: "商品编号",
            prop: "productGroupPurchase",
            render: ({ row }) => (
                <Button
                    aria-label="查看团购商品详情"
                    type="link"
                    onClick={() =>
                        void navigate(`/productManagement/groupPurchase/detail?id=${row.productGroupPurchase.id || ""}`)
                    }
                >
                    {row.productGroupPurchase.id || "-"}
                </Button>
            )
        },
        {
            key: "weight",
            label: "权重值",
            prop: "productGroupPurchase",
            render: ({ row }) => (
                <ProductWeightInput
                    aria-label="团购商品权重值"
                    controls={false}
                    max={10000}
                    min={0}
                    precision={0}
                    defaultValue={row.productGroupPurchase.weight ?? ""}
                    onBlur={event => {
                        const input = event.currentTarget as HTMLInputElement;
                        const weight = Number(input.value);
                        if (row.productGroupPurchase.id == null || input.value === "" || !Number.isFinite(weight))
                            return;
                        void saveWeight({ id: row.productGroupPurchase.id, weight });
                    }}
                />
            )
        },
        {
            key: "title",
            label: "主图/标题",
            prop: "productGroupPurchase",
            render: ({ row }) => (
                <Space>
                    {row.mainFile?.path ? (
                        <img alt="团购商品主图" className="h-8 w-8 object-contain" src={row.mainFile.path} />
                    ) : (
                        <span className="product-image-placeholder">暂无图片</span>
                    )}
                    <span>{row.productGroupPurchase.title || "-"}</span>
                </Space>
            )
        },
        {
            key: "productType",
            label: "品类",
            prop: "productType",
            render: ({ row }) =>
                row.productType?.name
                    ? `${row.productType.name}${row.productType.areaAbbreviation ? ` (${row.productType.areaAbbreviation})` : ""}`
                    : "-"
        },
        {
            key: "supplier",
            label: "供应商",
            prop: "productGroupPurchase",
            render: ({ row }) =>
                row.productGroupPurchase.supplierEnterprise?.enterpriseName ||
                row.productGroupPurchase.supplierEnterprise?.supplierName ||
                "-"
        },
        {
            key: "groupUnitPrice",
            label: "拼团单价（元/吨）",
            prop: "productGroupPurchase",
            render: ({ row }) => row.productGroupPurchase.groupUnitPrice ?? "-"
        },
        {
            key: "minOrderTon",
            label: "拼团最低吨数（吨）",
            prop: "productGroupPurchase",
            render: ({ row }) => row.productGroupPurchase.minOrderTon ?? "-"
        },
        {
            key: "groupSuccessTon",
            label: "拼团成功吨数（吨）",
            prop: "productGroupPurchase",
            render: ({ row }) => row.productGroupPurchase.groupSuccessTon ?? "-"
        },
        {
            key: "groupEndTime",
            label: "拼团截止时间",
            prop: "productGroupPurchase",
            render: ({ row }) => row.productGroupPurchase.groupEndTime || "-"
        },
        {
            key: "progress",
            label: "拼团进度",
            prop: "progress",
            render: ({ row }) => row.progress ?? "-"
        },
        {
            key: "handlerName",
            label: "商品专员",
            prop: "productGroupPurchase",
            render: ({ row }) => row.productGroupPurchase.handlerName || "-"
        },
        {
            key: "updatedTime",
            label: "更新时间",
            prop: "productGroupPurchase",
            render: ({ row }) => row.productGroupPurchase.updatedTime || "-"
        },
        {
            key: "groupPurchaseState",
            label: "团购状态",
            prop: "productGroupPurchase",
            render: ({ row }) => <Tag>{groupStateLabels[row.productGroupPurchase.groupPurchaseState || 0] || "-"}</Tag>
        },
        {
            key: "state",
            label: "上架状态",
            prop: "productGroupPurchase",
            render: ({ row }) => <Tag>{productStateLabels[row.productGroupPurchase.state || 0] || "-"}</Tag>
        },
        {
            key: "publishState",
            label: "发布状态",
            prop: "productGroupPurchase",
            render: ({ row }) => <Tag>{publishStateLabels[row.productGroupPurchase.publishState || 0] || "-"}</Tag>
        },
        {
            key: "operation",
            label: "操作",
            prop: "productGroupPurchase",
            render: ({ row }) => {
                const id = row.productGroupPurchase.id;
                const publishState = row.productGroupPurchase.publishState || 0;
                const productState = row.productGroupPurchase.state;
                const groupState = row.productGroupPurchase.groupPurchaseState;
                const canAssign = canManage && !(productState === 2 && publishState === 6);
                const canAudit = canManage && publishState === 1;
                const canEdit = canDeal && productState === 2 && [1, 2, 4, 5].includes(publishState);
                const canClose = canClosePermission && productState === 2 && [1, 2, 4, 5].includes(publishState);
                const canDownShelf = canDeal && productState === 1 && [3, 4].includes(publishState) && groupState === 1;
                const canCopy = canDeal && productState === 2 && publishState === 6 && [2, 3].includes(groupState || 0);
                if (!canAssign && !canAudit && !canEdit && !canClose && !canDownShelf && !canCopy) return null;
                return (
                    <Space>
                        {canAssign ? (
                            <ZhButton
                                aria-label="变更指派"
                                disabled={banButton}
                                size="small"
                                onClick={() => openAssign(row)}
                            >
                                变更指派
                            </ZhButton>
                        ) : null}
                        {canAudit ? (
                            <ZhButton
                                aria-label="审核商品"
                                disabled={banButton}
                                size="small"
                                onClick={() => void navigate(`/productManagement/groupPurchase/audit?id=${id || ""}`)}
                            >
                                审核
                            </ZhButton>
                        ) : null}
                        {canEdit ? (
                            <ZhButton
                                aria-label="编辑团购商品"
                                disabled={banButton}
                                size="small"
                                onClick={() => void navigate(`/productManagement/groupPurchase/edit?id=${id || ""}`)}
                            >
                                编辑
                            </ZhButton>
                        ) : null}
                        {canClose ? (
                            <ZhButton
                                aria-label="关闭团购商品"
                                disabled={banButton}
                                size="small"
                                onClick={() => void handleClose(row)}
                            >
                                关闭
                            </ZhButton>
                        ) : null}
                        {canDownShelf ? (
                            <ZhButton
                                aria-label="下架团购商品"
                                disabled={banButton}
                                size="small"
                                onClick={() => void openDownShelf(id)}
                            >
                                下架
                            </ZhButton>
                        ) : null}
                        {canCopy ? (
                            <ZhButton
                                aria-label="复制团购商品"
                                disabled={banButton}
                                size="small"
                                onClick={() => void navigate(`/productManagement/groupPurchase/add?copyId=${id || ""}`)}
                            >
                                复制
                            </ZhButton>
                        ) : null}
                    </Space>
                );
            }
        }
    ];

    const refresh = async () => {
        const result = await load(query);
        setItems(normalizeGroupPurchaseRows(result.list));
        setTotal(result.total || 0);
    };

    const handleClose = async (row: GroupPurchaseRow) => {
        const id = row.productGroupPurchase.id;
        if (id == null) return;
        // Vue 关闭接口要求清理主图、视频和附加图片的文件 ID。
        const unActiveFileIds = [
            row.mainFile?.id,
            row.videoFile?.id,
            ...(row.otherImgFile || []).map(file => file.id)
        ].filter((fileId): fileId is number => fileId != null);
        try {
            await close(id, unActiveFileIds);
            await refresh();
        } catch {
            // 关闭接口或刷新失败时保留当前团购商品列表，并反馈 Vue 页面同类操作错误。
            setErrorMessage("团购商品关闭失败");
        }
    };

    // 沿用 Vue groupTakeDownDialog：点击列表下架只打开提示并加载参团订单统计。
    const openDownShelf = async (id?: number | string) => {
        if (id == null) return;
        setDownShelfTargetId(id);
        setDownShelfInfo(undefined);
        setDownShelfError(null);
        setDownShelfLoading(true);
        try {
            setDownShelfInfo(await loadInfo(id));
        } catch {
            // 统计查询失败时保留确认入口，并把失败状态限制在当前弹窗内。
            setDownShelfError("团购下架订单信息加载失败");
        } finally {
            setDownShelfLoading(false);
        }
    };

    const cancelDownShelf = () => {
        setDownShelfTargetId(null);
        setDownShelfInfo(undefined);
        setDownShelfError(null);
    };

    const confirmDownShelf = async () => {
        if (downShelfTargetId == null) return;
        setDownShelfLoading(true);
        try {
            await removeFromShelf(downShelfTargetId);
            await refresh();
            cancelDownShelf();
        } catch {
            // 下架或刷新接口失败时保留弹窗，并反馈与 Vue 页面一致的失败状态。
            setDownShelfError("团购商品下架失败");
        } finally {
            setDownShelfLoading(false);
        }
    };

    const searchSpecialists = async (keyword: string) => {
        setAssignLoading(true);
        try {
            setAssignError(null);
            setSpecialists(await findSpecials(keyword));
        } catch {
            setAssignError("专员查询失败");
            setSpecialists([]);
        } finally {
            setAssignLoading(false);
        }
    };

    const openAssign = (row: GroupPurchaseRow) => {
        setAssignTarget(row);
        setSpecialistKeyword("");
        setSelectedHandlerId(row.productGroupPurchase.handlerId);
        void searchSpecialists("");
    };

    const handleAssign = async () => {
        if (!assignTarget || selectedHandlerId == null) {
            setAssignError("请选择指派的服务人员");
            return;
        }
        const id = assignTarget.productGroupPurchase.id;
        if (id == null) return;
        setAssignLoading(true);
        try {
            await assign(id, selectedHandlerId);
            setAssignTarget(null);
            await refresh();
        } catch {
            setAssignError("变更指派失败");
        } finally {
            setAssignLoading(false);
        }
    };

    return (
        <section aria-label="group-purchase-page" className="flex h-full min-h-full flex-col">
            <ZhDialog
                open={downShelfTargetId != null}
                title="提示"
                width={400}
                onCancel={cancelDownShelf}
                footer={
                    <Space>
                        <Button disabled={downShelfLoading} onClick={cancelDownShelf}>
                            取消
                        </Button>
                        <Button
                            aria-label="确认下架"
                            loading={downShelfLoading}
                            type="primary"
                            onClick={() => void confirmDownShelf()}
                        >
                            确认
                        </Button>
                    </Space>
                }
            >
                <p>是否确认下架，下架后已参团订单将会全部取消</p>
                {downShelfInfo?.totalOrderNum ? (
                    <div className="mt-3 rounded-md bg-gray-100 p-4">
                        目前有 <span className="text-red-500">{downShelfInfo.totalOrderNum}</span> 单参与，
                        <span className="text-red-500">{downShelfInfo.obligationOrderNum || 0}</span> 单待付款，
                        <span className="text-red-500">{downShelfInfo.toBeConfirmedOrderNum || 0}</span> 单待确认支付，
                        <span className="text-red-500">{downShelfInfo.groupingOrderNum || 0}</span> 单拼团中
                    </div>
                ) : null}
                {downShelfError ? <p role="alert">{downShelfError}</p> : null}
            </ZhDialog>
            <ZhDialog
                open={Boolean(assignTarget)}
                title="变更指派商品专员"
                width={842}
                onCancel={() => setAssignTarget(null)}
                footer={
                    <Space>
                        <Button onClick={() => setAssignTarget(null)}>取消</Button>
                        <Button
                            aria-label="确认指派"
                            loading={assignLoading}
                            type="primary"
                            onClick={() => void handleAssign()}
                        >
                            确认指派
                        </Button>
                    </Space>
                }
            >
                <Space.Compact block>
                    <Input
                        aria-label="专员姓名"
                        placeholder="请输入姓名"
                        value={specialistKeyword}
                        onChange={event => setSpecialistKeyword(event.target.value)}
                    />
                    <Button type="primary" onClick={() => void searchSpecialists(specialistKeyword)}>
                        查询专员
                    </Button>
                </Space.Compact>
                {assignError ? <p role="alert">{assignError}</p> : null}
                <div aria-label="专员列表" className="max-h-72 overflow-auto" role="radiogroup">
                    {specialists.map(specialist => {
                        const personId = specialist.user.personId;
                        if (personId == null) return null;
                        return (
                            <label key={personId} className="mt-3 block">
                                <input
                                    aria-label={specialist.user.name || String(personId)}
                                    checked={selectedHandlerId === personId}
                                    name="group-purchase-handler"
                                    type="radio"
                                    onChange={() => setSelectedHandlerId(personId)}
                                />
                                <span className="ml-2">
                                    <span>{specialist.user.name || "未命名专员"}</span> (
                                    {specialist.user.employeeId || "-"})
                                </span>
                            </label>
                        );
                    })}
                </div>
            </ZhDialog>
            <Card className="flex h-full flex-1 flex-col [&_.ant-card-body]:flex [&_.ant-card-body]:min-h-0 [&_.ant-card-body]:flex-1 [&_.ant-card-body]:flex-col">
                <ZhTable<GroupPurchaseRow>
                    data={items}
                    columns={columns}
                    emptyText="暂无团购商品"
                    pagination={false}
                    rowKey={row => String(row.productGroupPurchase.id)}
                    isShowResetBtn={false}
                    isShowSearchBtn={false}
                    tableHeader={
                        <div className="admin-table-composed-header">
                            <Typography.Title className="mb-4 mt-0" level={4}>
                                团购商品管理
                            </Typography.Title>

                            {errorMessage ? (
                                <Alert className="mb-4" message={errorMessage} role="alert" type="error" />
                            ) : null}

                            <Form
                                className="admin-table-filter-form mb-4"
                                form={filterForm}
                                layout="inline"
                                onFinish={values => submitFilters(values)}
                            >
                                {canDeal ? (
                                    <Button
                                        disabled={banButton}
                                        type="primary"
                                        onClick={() => void navigate("/productManagement/groupPurchase/add")}
                                    >
                                        新增团购商品
                                    </Button>
                                ) : null}
                                <Form.Item name="keyword">
                                    <Input placeholder="请输入商品编号或商品标题" />
                                </Form.Item>
                                <Button
                                    aria-label="搜索"
                                    htmlType="submit"
                                    icon={<TableSearchIcon name="search" />}
                                    type="primary"
                                >
                                    搜索
                                </Button>
                                <Button
                                    aria-label="重置"
                                    className="table-filter-reset-button"
                                    icon={<TableSearchIcon name="reset" />}
                                    title="重置"
                                    onClick={resetFilters}
                                />
                            </Form>
                        </div>
                    }
                    append={
                        <div className="admin-table-composed-pagination">
                            <ZhPagination
                                current={query.page}
                                pageSize={query.size}
                                total={total}
                                showTotal={value => `共${value}条数据`}
                                pageSizeOptions={[10, 20, 30, 40, 50, 100]}
                                showSizeChanger
                                // 沿用 Vue 团购商品分页契约，切换页大小后回到第 1 页。
                                onChange={(page, pageSize) =>
                                    setQuery(currentQuery => ({
                                        ...currentQuery,
                                        page: pageSize !== currentQuery.size ? 1 : page,
                                        size: pageSize
                                    }))
                                }
                            />
                        </div>
                    }
                />
            </Card>
        </section>
    );
}

export default GroupPurchasePage;
